import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, CardMedia, IconButton, Stack, Typography } from '@mui/material'
import React from 'react'
import { SystemConfigurationComponentsResponse } from 'src/Libs/shop/models'
import { makeStyles } from 'tss-react/mui'
import { J3Onoff } from '@e3dc-react/icons'
import { useDispatch } from 'react-redux'
import { removeProduct } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationAction'
import { getImageUrlBySize } from 'src/Libs/shop'
import { ShopImageThumnbailSizes } from 'src/Libs/shop/types'
import { Component } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationModel'

interface AdditionalComponentSystemConfigurationCardProps {
  mainComponent: SystemConfigurationComponentsResponse | null
  selectedMainComponents: Component[] | null
}

const useStyles = makeStyles()(theme => ({
  mainComponentWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '8px',
    marginTop: theme.spacing(2),
    minWidth: '400px',
    flex: '1 auto',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-between',
      minWidth: 0,
    },
  },

  icon: {
    color: theme.palette.text.headerTitle,
    transform: 'rotate(90deg)',
  },
  spaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
  },
  quantityNumber: {
    wordWrap: 'break-word',
    maxWidth: '130px',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
  },
}))

const QUANTITY_SYMBOL = 'X'

const AdditionalComponentSystemConfigurationCard: React.FC<AdditionalComponentSystemConfigurationCardProps> = ({
  mainComponent,
  selectedMainComponents,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const dispatch = useDispatch()

  const optionalCategories = mainComponent?.components.filter(category => !category.mandatory) ?? []
  const selectedProducts = selectedMainComponents?.flatMap(component => component.products) ?? []

  const hasSelectedOptionalProducts = optionalCategories.some(category =>
    category.products.some(productWrapper => selectedProducts.some(selectedProduct => selectedProduct.id === productWrapper.product.id))
  )

  const handleRemoveProduct = (
    categoryId: string,
    productId: string | null | undefined,
    selectedCategoryIndex: number,
    selectedProductQuantity: { id: string } | null | undefined
  ): void => {
    if (productId && selectedCategoryIndex !== -1 && selectedProductQuantity?.id === productId) {
      dispatch(removeProduct(categoryId, productId))
    }
  }

  return (
    <Stack spacing={2}>
      {optionalCategories.map(category => {
        const selectedProductsForCategory = category.products.filter(productWrapper =>
          selectedProducts.some(selectedProduct => selectedProduct.id === productWrapper.product.id)
        )

        if (selectedProductsForCategory.length === 0) {
          return null
        }

        const selectedCategoryIndex = selectedMainComponents?.findIndex(component => component.id === category.id) ?? -1

        return (
          <Box key={category.id}>
            <Typography variant="h5" mb={2}>
              {category.translated.name}
            </Typography>

            {selectedProductsForCategory.map(productWrapper => {
              const selectedProductQuantity =
                selectedCategoryIndex === -1
                  ? null
                  : selectedMainComponents?.[selectedCategoryIndex]?.products.find(product => product.id === productWrapper.product.id)

              const productImageUrl = getImageUrlBySize(productWrapper.product?.cover?.media?.thumbnails, ShopImageThumnbailSizes.ExtraSmall)

              return (
                <Box key={productWrapper.product.id} className={classes.mainComponentWrapper}>
                  <Box className={classes.spaceBetween}>
                    <Box>
                      <CardMedia component="img" image={productImageUrl} sx={{ maxWidth: '45px', height: 'auto' }} />
                      <Typography variant="h5" align="center" color="primary">
                        {selectedProductQuantity?.qty + QUANTITY_SYMBOL}
                      </Typography>
                    </Box>

                    <Typography variant="h4" className={classes.quantityNumber}>
                      {productWrapper.product.translated.name}
                    </Typography>
                  </Box>

                  <IconButton
                    onClick={() => {
                      handleRemoveProduct(category.id, selectedProductQuantity?.id, selectedCategoryIndex, selectedProductQuantity)
                    }}
                  >
                    <J3Onoff fontSize="large" className={classes.icon} />
                  </IconButton>
                </Box>
              )
            })}
          </Box>
        )
      })}

      {(optionalCategories.length === 0 || !hasSelectedOptionalProducts) && (
        <Typography variant="h4">{t('shop.system-configure-card.not-selected-yet')}</Typography>
      )}
    </Stack>
  )
}

export default AdditionalComponentSystemConfigurationCard
